<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { app as appInfo } from '@shared/lib/app'
import ChangePassword from './ChangePassword.vue'
import { clock, dayKey, dayLabel, groupByDay, range, stateAt, untilText, type WindowState } from '@shared/lib/schedule'
import type { MyExam, MySchedule } from '../types'

// The pupil's own exams, by day. It opens when no exam is open yet at sign-in, when several are,
// and after an exam is finished. Plain HTML like the rest of the pupil path.
const props = withDefaults(
  defineProps<{
    schedule: MySchedule
    /** True when the pupil signed in with a password here, so "Mulai" can sign them into an exam. */
    canStart: boolean
    busy?: boolean
    notice?: string
    error?: string
    /** Label of the button that leaves this screen. */
    backLabel?: string
    /** The pupil's sign-in token when there is one, so they can change their password here. */
    authToken?: string
  }>(),
  { busy: false, notice: '', error: '', backLabel: 'Kembali' },
)

const emit = defineEmits<{
  start: [sessionId: string]
  refresh: []
  back: []
}>()

// The server clock, so the screen is right even when the pupil's device clock is off.
const nowMs = ref(Date.now())
const offset = ref(0)
let tick: number | undefined
let refreshTimer: number | undefined

function syncClock(): void {
  offset.value = Date.parse(props.schedule.server_time) - Date.now()
  nowMs.value = Date.now() + offset.value
}

watch(() => props.schedule.server_time, syncClock, { immediate: true })

onMounted(() => {
  tick = window.setInterval(() => {
    nowMs.value = Date.now() + offset.value
  }, 15000)
  // Ask again every minute: a new exam may have been opened or closed by the proctor.
  refreshTimer = window.setInterval(() => {
    if (!document.hidden && !props.busy) emit('refresh')
  }, 60000)
})
onBeforeUnmount(() => {
  window.clearInterval(tick)
  window.clearInterval(refreshTimer)
})

interface Row extends MyExam {
  state: WindowState
}

const rows = computed<Row[]>(() =>
  props.schedule.exams.map((exam) => ({ ...exam, state: stateAt(exam.window_start, exam.window_end, nowMs.value) })),
)

const zone = computed(() => props.schedule.time_zone)
const nowIso = computed(() => new Date(nowMs.value).toISOString())
const today = computed(() => dayKey(nowIso.value, zone.value))
const days = computed(() => groupByDay(rows.value, (row) => row.window_start, zone.value))

// What matters right now: an exam to do (open and not finished), else the next one coming.
const current = computed(() => rows.value.find((row) => row.state === 'open' && row.status !== 'finished') ?? null)
const next = computed(() => rows.value.find((row) => row.state === 'upcoming' && row.status !== 'finished') ?? null)
const allDone = computed(() => rows.value.length > 0 && rows.value.every((row) => row.status === 'finished'))
const finished = computed(() => rows.value.filter((row) => row.status === 'finished').length)

// The app passes a note only when it has one (the result screen does). Otherwise say what is going on.
const message = computed(() => {
  if (props.notice) return props.notice
  if (!props.canStart) return ''
  if (current.value && rows.value.filter((row) => row.state === 'open' && row.status !== 'finished').length > 1) {
    return 'Anda punya lebih dari satu ujian yang dibuka. Pilih yang akan dikerjakan.'
  }
  if (!current.value && !allDone.value) return 'Belum ada ujian yang dibuka untuk Anda saat ini.'
  return ''
})

function isToday(key: string): boolean {
  return key === today.value
}

function whenText(row: Row): string {
  if (!row.window_start) return ''
  return `${dayLabel(row.window_start, zone.value)}, pukul ${clock(row.window_start, zone.value)}`
}

function badge(row: Row): { text: string; kind: string } {
  if (row.status === 'finished') return { text: 'Selesai', kind: 'done' }
  if (row.state === 'open') return row.status === 'in_progress' ? { text: 'Sedang dikerjakan', kind: 'working' } : { text: 'Dibuka', kind: 'open' }
  if (row.state === 'upcoming') return { text: row.window_start ? `Mulai ${untilText(row.window_start, nowMs.value, zone.value)}` : 'Akan datang', kind: 'upcoming' }
  return { text: 'Terlewat', kind: 'missed' }
}

function startLabel(row: Row): string {
  return row.status === 'in_progress' ? 'Lanjutkan ujian' : 'Mulai ujian'
}
</script>

<template>
  <div class="schedule-shell">
    <header class="schedule-header">
      <div class="brand">
        <span class="brand-mark" aria-hidden="true">OAP</span>
        <span class="brand-text">
          <strong>{{ schedule.school || appInfo.name }}</strong>
          <small>Jadwal ujian</small>
        </span>
      </div>
      <div class="who">
        <strong>{{ schedule.participant.name }}</strong>
        <small>{{ [schedule.participant.class, schedule.participant.room].filter(Boolean).join(' · ') }}</small>
      </div>
    </header>

    <main id="main-content" class="schedule-main">
      <p v-if="message" class="banner banner--info" role="status">{{ message }}</p>
      <p v-if="error" class="banner banner--error" role="alert">{{ error }}</p>

      <section class="focus" :class="{ 'focus--open': current, 'focus--done': allDone }" aria-live="polite">
        <template v-if="current">
          <p class="eyebrow">Sedang dibuka</p>
          <h1>{{ current.title }}</h1>
          <p class="focus__meta">
            {{ current.window_start && current.window_end ? range(current.window_start, current.window_end, zone) : '' }} · {{ current.duration_minutes }} menit · {{ current.items }} soal
          </p>
          <button v-if="canStart" class="button button--primary" type="button" :disabled="busy" @click="emit('start', current.session_id)">
            <span v-if="busy" class="spinner" aria-hidden="true" />
            {{ busy ? 'Mohon tunggu…' : startLabel(current) }}
          </button>
          <p v-else class="focus__hint">Untuk mengerjakan, masuk kembali dengan ID dan kata sandi Anda.</p>
          <p v-if="current.token_required && canStart" class="focus__hint">Siapkan token dari pengawas di ruang ujian.</p>
        </template>
        <template v-else-if="next">
          <p class="eyebrow">Ujian berikutnya</p>
          <h1>{{ next.title }}</h1>
          <p class="focus__meta">{{ whenText(next) }}</p>
          <p class="focus__count">{{ next.window_start ? untilText(next.window_start, nowMs, zone) : '' }}</p>
        </template>
        <template v-else-if="allDone">
          <p class="eyebrow">Selesai</p>
          <h1>Semua ujian Anda sudah selesai</h1>
          <p class="focus__meta">{{ finished }} dari {{ rows.length }} ujian terkumpul.</p>
        </template>
        <template v-else>
          <p class="eyebrow">Jadwal</p>
          <h1>Tidak ada ujian yang akan datang</h1>
          <p class="focus__meta">Hubungi pengawas bila ada ujian yang belum Anda kerjakan.</p>
        </template>
      </section>

      <section v-for="group in days" :key="group.key" class="day" :aria-labelledby="`day-${group.key}`">
        <h2 :id="`day-${group.key}`" class="day__title" :class="{ 'day__title--today': isToday(group.key) }">
          {{ group.label }}
          <span v-if="isToday(group.key)" class="today-tag">Hari ini</span>
        </h2>
        <ul class="exam-list">
          <li v-for="row in group.items" :key="row.session_id" class="exam" :class="[`exam--${badge(row).kind}`]">
            <div class="exam__time">
              {{ row.window_start && row.window_end ? range(row.window_start, row.window_end, zone) : '' }}
            </div>
            <div class="exam__body">
              <strong>{{ row.title }}</strong>
              <small>{{ row.duration_minutes }} menit · {{ row.items }} soal</small>
            </div>
            <div class="exam__state">
              <span class="chip" :class="`chip--${badge(row).kind}`">{{ badge(row).text }}</span>
              <button
                v-if="canStart && row.state === 'open' && row.status !== 'finished' && row.session_id !== current?.session_id"
                class="button button--secondary button--small"
                type="button"
                :disabled="busy"
                @click="emit('start', row.session_id)"
              >
                {{ startLabel(row) }}
              </button>
            </div>
          </li>
        </ul>
      </section>

      <div class="actions">
        <button class="button button--ghost" type="button" :disabled="busy" @click="emit('refresh')">Perbarui</button>
        <button class="button button--secondary" type="button" @click="emit('back')">{{ backLabel }}</button>
        <ChangePassword v-if="authToken" :token="authToken" />
      </div>
    </main>
  </div>
</template>

<style scoped>
.schedule-shell {
  min-height: 100vh;
}

.schedule-header {
  display: flex;
  width: min(760px, 100%);
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 0 auto;
  padding: 20px 20px 6px;
}

.who {
  display: grid;
  text-align: right;
}

.who small {
  color: var(--muted);
}

.schedule-main {
  display: grid;
  width: min(760px, 100%);
  gap: 18px;
  margin: 0 auto;
  padding: 12px 20px 54px;
}

.focus {
  padding: clamp(20px, 4vw, 30px);
  border: 1px solid var(--line);
  border-radius: 20px;
  background: var(--surface);
  box-shadow: var(--shadow);
}

.focus--open {
  border-color: #8fd0c4;
  background: linear-gradient(180deg, var(--accent-soft), var(--surface) 70%);
}

.focus--done {
  border-color: #a6dbbd;
  background: var(--success-soft);
}

.focus h1 {
  margin: 0 0 6px;
  font-size: clamp(1.2rem, 3.4vw, 1.55rem);
  line-height: 1.25;
}

.focus__meta {
  margin: 0 0 14px;
  color: var(--ink-soft);
}

.focus__count {
  margin: 0;
  color: var(--accent-strong);
  font-size: 1.05rem;
  font-weight: 750;
}

.focus__hint {
  margin: 12px 0 0;
  color: var(--muted);
  font-size: 0.88rem;
}

.day__title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 6px 0 10px;
  color: var(--ink-soft);
  font-size: 0.95rem;
  font-weight: 750;
}

.day__title--today {
  color: var(--accent-strong);
}

.today-tag {
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--accent);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 750;
}

.exam-list {
  display: grid;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.exam {
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr) auto;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid var(--line);
  border-radius: 14px;
  background: var(--surface);
}

.exam--open,
.exam--working {
  border-color: #8fd0c4;
  box-shadow: 0 0 0 3px var(--accent-soft);
}

.exam--done {
  background: var(--surface-soft);
}

.exam--missed {
  border-color: #efc3bf;
  background: var(--danger-soft);
}

.exam__time {
  font-variant-numeric: tabular-nums;
  font-weight: 750;
  color: var(--ink);
}

.exam__body {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.exam__body small {
  color: var(--muted);
}

.exam__state {
  display: grid;
  justify-items: end;
  gap: 8px;
}

.chip {
  padding: 4px 11px;
  border-radius: 999px;
  background: var(--surface-soft);
  border: 1px solid var(--line);
  color: var(--ink-soft);
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

.chip--open,
.chip--working {
  border-color: transparent;
  background: var(--accent);
  color: #fff;
}

.chip--done {
  border-color: transparent;
  background: var(--success-soft);
  color: var(--success);
}

.chip--missed {
  border-color: transparent;
  background: var(--danger-soft);
  color: var(--danger);
}

.button--small {
  min-height: 36px;
  padding: 6px 12px;
  font-size: 0.85rem;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: space-between;
}

@media (max-width: 560px) {
  .exam {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
  }

  .exam__state {
    grid-auto-flow: column;
    justify-content: space-between;
    justify-items: start;
    align-items: center;
  }

  .schedule-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .who {
    text-align: left;
  }
}
</style>
